import React, { useState, useEffect, useRef } from 'react';

import { ToastsStore } from '@bnsf/bnsf-react-toast-messages';
import Select from 'react-select';
import DateTime from '@bnsf/bnsf-date-time-picker';
import moment from 'moment';

import { DATE_FORMAT, TIME_FORMAT } from '../../../../constants/appConstants';
import { WIN_TYPES, INFORMATIONAL_GANGIDS, DIVISION_MIN_DATE_TIME_CONFIG } from '../../../../constants/winConstants';
import { fromObject } from '../../../../utils/selectUtil';
import { getDivisions, getGangIds } from '../../../../services/trackWindow';
import './WinEditStep1.css';
import { useDispatch, useSelector } from 'react-redux';
import ActionTypes from '../../../WindowQuery/constants';

const TIME_REGEX = new RegExp('^(?<hour>[01]?[0-9]|2[0-3]):?(?<minute>[0-5][0-9])$');
const DEFAULT_MIN_DATE = moment().day(7);

let __gang_ids = [];

const WinEditStep1 = (props) => {
    const { divisionList, selectedDivision } = useSelector(state => state.windowQuery);
    const [gangIds, setGangIds] = useState([...__gang_ids]);
    const [selectedGangs, setSelectedGangs] = useState([]);
    const [selectedWinType, setSelectedWinType] = useState('');
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [duration, setDuration] = useState('');
    const [informationalIncluded, setInformationalIncluded] = useState(false);

    const [dateOptions, setDateOptions] = useState({
        format: DATE_FORMAT,
        minDate: DEFAULT_MIN_DATE,
        defaultDate: ''
    });

    const refGangIds = useRef(null);
    const refDivisions = useRef(null);
    const refStartDate = useRef(null);
    const dispatch = useDispatch();


    useEffect(() => {
        if (selectedDivision) {
            setMinDateOnBasisOfDivision(selectedDivision);
        }
        if (divisionList.length === 0) {
            fetchDivisions();
        }
        if (__gang_ids.length === 0) {
            fetchGangIds();
        }
    }, []);

    useEffect(() => {
        let gangOptions = [];

        if (props.data && gangIds.length > 0) {
            gangOptions = gangIds.filter(gangOption => props.data.gangIds.includes(gangOption.value));
            setSelectedGangs(gangOptions);

            setSelectedWinType(props.data.winType);
            setStartDate(props.data.startDate);
            setStartTime(props.data.startTime);
            setDuration(props.data.duration);
        }

    }, [gangIds, props.data.winType]);

    const fetchDivisions = () => {
        getDivisions().then(divsResponse => {
            const divs = divsResponse.map(aDiv => ({
                label: aDiv.name,
                value: aDiv.code
            }));
            props.setDivList(divs);
            const sQueryParams = localStorage.getItem('queryParams');
            if (sQueryParams) {
                const queryParams = JSON.parse(sQueryParams);
                if (queryParams.division) {
                    props.setSelectedDiv(queryParams.division);
                }
            }
        }, onDivisionFailure)
    }

    const onDivisionFailure = () => ToastsStore.showToast("DIVISION_FAILURE");

    const onDivisionChange = (selected) => {
        setStartDate('');
        if (selectedDivision !== selected?.value) {
            const div = selected ? selected.value : '';
            dispatch({ type: ActionTypes.SET_SELECTED_DIVISION, selectedDivision: div });
            if (div) {
                setMinDateOnBasisOfDivision(div);
            } else {
                ToastsStore.showToast("Division is required");
            }
        }
    }

    /**
    * Sets the minimum date based on the division code.
    * If Division is Kansas : 
    *   if it is before 3pm on Wednesday, set minDate to Sunday next week
    *   otherwise, set minDate to Sunday the week after next week
    * If Division is NOT Kansas : 
    *   if it is before midnight on Tuesday, set minDate to Sunday next week
    *   otherwise, set minDate to Sunday the week after next week
    */
    const setMinDateOnBasisOfDivision = (divisionCode) => {
        const today = moment();
        const todayDay = today.day();
        const todayTime = today.hour() * 60 + today.minute();
        let minDate = DEFAULT_MIN_DATE;

        const minDateTimeConfig = DIVISION_MIN_DATE_TIME_CONFIG[divisionCode];

        if (minDateTimeConfig) {
            const { deadlineDay, deadlineTime } = minDateTimeConfig;
            const minTime = parseInt(deadlineTime.split(':')[0]) * 60 + parseInt(deadlineTime.split(':')[1]);
            const dayOfWeek = moment().day(deadlineDay).day();
            const weeksToAdd = (todayDay < dayOfWeek || (todayDay === dayOfWeek && todayTime < minTime)) ? 1 : 2;
            minDate = today.clone().add(weeksToAdd, 'weeks').day(0);
        } else {
            const defaultMinDateTimeConfig = DIVISION_MIN_DATE_TIME_CONFIG['DEFAULT'];
            const { deadlineDay, deadlineTime } = defaultMinDateTimeConfig;
            const minTime = parseInt(deadlineTime.split(':')[0]) * 60 + parseInt(deadlineTime.split(':')[1]);
            const dayOfWeek = moment().day(deadlineDay).day();
            const weeksToAdd = (todayDay < dayOfWeek || (todayDay === dayOfWeek && todayTime < minTime)) ? 1 : 2;
            minDate = today.clone().add(weeksToAdd, 'weeks').day(0);
        }

        setDateOptions(prevOptions => ({
            ...prevOptions,
            minDate
        }));
    }

    const fetchGangIds = () => {
        getGangIds().then(svcResponse => {
            const gids = svcResponse
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(aGang => ({
                    label: aGang.name,
                    value: aGang.name
                }));

            __gang_ids = [...gids];

            setGangIds(gids);
        }, onGangIdFailure);
    }

    const onGangIdFailure = () => ToastsStore.showToast("GANGID_FAILURE");
    const onGangSelected = (selected) => {
        setSelectedGangs(selected);

        let newGangs = selected.map(opt => opt.value);

        if (newGangs.length <= 5) {
            props.updateWin({ gangIds: newGangs.join('/') });
        }

        // check to see if gangId is subset of INFORMATIONAL_GANGIDS 
        let infoIncluded = newGangs.length > 0 && newGangs.every(gang => INFORMATIONAL_GANGIDS.includes(gang.toUpperCase()));

        // reset selectedWinType if it was set to Informational Window, and
        // some gang id is not in INFORMATIONAL_GANGIDS
        if (!infoIncluded && selectedWinType === 'IW') {
            setSelectedWinType('');
        }

        // reset informationalIncluded with new gang id selection
        if (infoIncluded !== informationalIncluded) {
            setInformationalIncluded(infoIncluded);
        }
    }

    const onWinTypeSelected = (selected) => {
        setSelectedWinType(selected.value);

        props.updateWin({ winType: selected.value });
    }

    const onFocus_date = () => {
        refStartDate.current.blur();
    }

    const onChange_date = (newDate) => {
        setStartDate(newDate);

        props.updateWin({ startDate: newDate });
    }

    const onChange_time = (evt) => {
        const newTime = evt.target.value;
        setStartTime(newTime);

        if (!newTime) return;

        const tmSplit = newTime.split(':');
        if ((tmSplit.length === 1 && newTime.length >= 4) ||
            (tmSplit.length > 1 && newTime.length >= 5)) {
            validateTime(evt);
        }
    }

    const onBlur_time = (evt) => {
        const newTime = evt.target.value;

        if (!newTime) return;
        const match = validateTime(evt);
        if (match) {
            let h = match.groups.hour.padStart(2, '0');
            let m = match.groups.minute;

            const standardTime = [h, m].join(':');
            props.updateWin({ startTime: standardTime });
            setStartTime(standardTime);
        }
    }

    const validateTime = (evt) => {
        const newTime = evt.target.value;
        let match = newTime && newTime.match(TIME_REGEX);

        if (match) {
            evt.target.classList.remove('bwwp-invalid');
        }
        else {
            // invalid time format
            evt.target.classList.add('bwwp-invalid');
        }

        return match;
    }

    /**
     * Auto-round the duration to nearest whole/quarter hour
     */
    const onChange_duration = (evt) => {
        let timer = null;
        let funcRoundToQuarterHour = num => Math.round(num / 0.25) * 0.25;

        setDuration(evt.target.value);

        // delay round operation to allow user to complete the input
        clearTimeout(timer);
        timer = setTimeout(() => {
            let newDuration = evt.target.value;

            if (newDuration === '') {
                return;
            }
            newDuration = funcRoundToQuarterHour(newDuration);
            setDuration(newDuration);
            // window duration > 0, but no upper limit
            if (parseFloat(newDuration) > 0) {
                evt.target.classList.remove('bwwp-invalid');
                props.updateWin({ duration: newDuration });
            }
            else {
                // invalid duration
                evt.target.classList.add('bwwp-invalid');
            }
        }, 1000);
    }

    const goNext = () => {
        props.onNext();
    };

    const onCancel = () => {
        props.onCancel();
    };

    // Requirement: 
    //  cannot create/edit windows planned for current week
    //  cannot create/edit windows planned for next week after Wednesday at midnight
    // const dateOptions = {
    //     format: DATE_FORMAT,
    //     minDate: minDate,
    //     defaultDate: ''
    // };

    /**
     * Can only proceed to next step when both below conditions are met
     *  1. all controls have set value
     *  2. valid value set for gang id, startTime and duration
     * @returns Boolean
     */
    const canNext = () => {
        let result = selectedDivision && selectedGangs.length > 0 && selectedWinType && startDate && startTime && duration;

        result = result && selectedGangs.length <= 5 && parseFloat(duration) > 0 && startTime.match(TIME_REGEX);

        return result;
    }

    const winTypeOptions = fromObject(WIN_TYPES, [informationalIncluded ? '' : 'IW']);

    return (
        <div className='clsWinEditStep clsWinEditStep1'>
            <div className='row'>
                <div className='col-md-6 col-sm-12 pr-md-1 '>
                    <div className='clsWinEditStep-ctrl-label'>Division</div>
                    <Select
                        ref={refDivisions}
                        name='gangIds'
                        options={divisionList}
                        noOptionsMessage={() => 'No Result Found'}
                        value={selectedDivision}
                        onChange={onDivisionChange}
                        className={"basic-multi-select"}
                        classNamePrefix="select" />
                    {/* <span className='invalid-message'>Please select division.</span> */}
                </div>
                <div className='col-md-6 col-sm-12 pl-md-0'>
                    <div className='clsWinEditStep-ctrl-label'>Gang ID*</div>
                    <Select multi
                        ref={refGangIds}
                        name='gangIds'
                        options={gangIds}
                        noOptionsMessage={() => 'No Result Found'}
                        value={selectedGangs}
                        onChange={onGangSelected}
                        className={"basic-multi-select " + (selectedGangs.length > 5 ? "bwwp-invalid" : "")}
                        classNamePrefix="select" />
                    <span className='invalid-message'>Please select up to 5 gang ids.</span>
                </div>
            </div>
            <div className='clsWinEditStep-block'>
                <div className='clsWinEditStep-ctrl-label'>Window Type*</div>
                <Select
                    name='winType'
                    options={winTypeOptions}
                    noOptionsMessage={() => 'No Result Found'}
                    value={selectedWinType}
                    onChange={onWinTypeSelected} />
            </div>
            <div className='clsWinEditStep-block'>
                <div className='clsFlex'>
                    <div className='clsWinEditStep-ctrl-label plr-0 w-60'>Date*</div>
                    <div className='clsWinEditStep-ctrl-label plr-0 w-40'>Time*</div>
                </div>
                <div className='clsFlex'>
                    <div className='plr-0 w-60'>
                        <DateTime key={"winEditStep_date"}
                            placeholder={DATE_FORMAT} pickerOptions={dateOptions}
                            value={startDate}
                            ref={refStartDate}
                            onDateChange={onChange_date}
                            onFocus={onFocus_date}
                        />
                    </div>
                    <div className='plr-0 w-40'>
                        <input type="text" className="form-control" placeholder={TIME_FORMAT} value={startTime} onChange={onChange_time} onBlur={onBlur_time} />
                        <span className='invalid-message'>Invalid time. Please follow the format of HHmm or HH:mm</span>
                    </div>
                </div>
            </div>
            <div className='clsWinEditStep-block'>
                <div className='clsWinEditStep-ctrl-label'>Duration*</div>
                <input type="number" className="form-control" min="1" max="8" value={duration} onChange={onChange_duration} />
                <span className='invalid-message'>Duration must be greater than 0.</span>
            </div>
            <div className='clsWinEditStep-block'>
                <div className='clsWinEditStep-note'>*: Required field</div>
            </div>
            <div className='clsWinEditStep-block'>
                <button className='btn btn-bwwp-secondary' onClick={onCancel}>CANCEL</button>
                <button className='btn btn-raised btn-bwwp-primary' disabled={!canNext()} onClick={goNext}>NEXT</button>
            </div>
        </div>
    );
};

export default WinEditStep1;